import { t, type I18nLocale } from '@ardoq/i18n';
import {
  RelationshipsNode,
  RelationshipsViewDependencies,
  RelationshipsViewProperties,
  RelationshipsViewSettings,
} from '../types';
import {
  DropdownConfig,
  SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import {
  bundleRelationshipsHandler,
  getCollapseAllButtonConfig,
  getExpandAllButtonConfig,
  toActiveGroups,
} from '../sharedControlsConfigs';
import { DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import { isRecord } from '@ardoq/common-helpers';
import { hasOwnProperty } from 'utils/objectUtils';
import { hasGraphItemsGroups, hasViewInstanceId } from '@ardoq/graph';

export const toRelationshipsViewDependencies = ({
  viewSettings,
  groups,
  rootNode,
  isViewpointMode,
  componentTypes,
  viewInstanceId,
}: RelationshipsViewProperties): RelationshipsViewDependencies => ({
  viewSettings,
  groups: { byId: groups.byId, add: groups.add, update: groups.update },
  rootNode,
  isViewpointMode,
  componentTypes,
  viewInstanceId,
});

export type ViewpointModeRelationshipsSettingsControlsDependencies = {
  viewSettings: RelationshipsViewSettings;
  rootNode: RelationshipsNode | null;
  activeGroups: string[];
  viewInstanceId: string;
};

type ToViewpointModeRelationshipsSettingsControlsDependenciesArgs = Pick<
  RelationshipsViewDependencies,
  'viewSettings' | 'groups' | 'rootNode' | 'viewInstanceId'
>;

export const isRelationshipsSettingsControlsDependencies = (
  viewProperties: Record<string, unknown>
): viewProperties is RelationshipsViewDependencies => {
  const { viewSettings, isViewpointMode, componentTypes } = viewProperties;

  return (
    hasGraphItemsGroups(viewProperties) &&
    hasViewInstanceId(viewProperties) &&
    isRecord(viewSettings) &&
    typeof viewSettings.bundleRelationships === 'boolean' &&
    hasOwnProperty(viewProperties, 'rootNode') &&
    typeof isViewpointMode === 'boolean' &&
    Array.isArray(componentTypes)
  );
};

export const toViewpointModeRelationshipsSettingsControlsDependencies = ({
  viewSettings,
  groups,
  rootNode,
  viewInstanceId,
}: ToViewpointModeRelationshipsSettingsControlsDependenciesArgs): ViewpointModeRelationshipsSettingsControlsDependencies => ({
  viewSettings,
  rootNode,
  viewInstanceId,
  activeGroups: toActiveGroups(groups),
});

type ToViewpointModeRelationshipsViewModifiersArgs = {
  viewSettings: RelationshipsViewSettings;
  i18nLocale: I18nLocale;
};

export const toViewpointModeRelationshipsViewModifiers = ({
  viewSettings,
  i18nLocale,
}: ToViewpointModeRelationshipsViewModifiersArgs): SettingsConfig[] => {
  const { bundleRelationships } = viewSettings;

  return [
    {
      id: 'bundleRelationships',
      label: t(
        i18nLocale,
        'relationsD3View:settingsBar.referenceLines',
        'Reference Lines'
      ),
      iconName: IconName.SEPARATE_REFERENCES,
      isActive: bundleRelationships,
      type: SettingsType.DROPDOWN,
      options: [
        {
          label: t(
            i18nLocale,
            'relationsD3View:settingsBar.combineReferenceLines',
            'Combine reference lines'
          ),
          type: DropdownOptionType.OPTION,
          isActive: bundleRelationships,
          onClick: bundleRelationshipsHandler(true),
        } satisfies DropdownOption,
        {
          label: t(
            i18nLocale,
            'relationsD3View:settingsBar.separateReferenceLines',
            'Separate reference lines'
          ),
          type: DropdownOptionType.OPTION,
          isActive: !bundleRelationships,
          onClick: bundleRelationshipsHandler(false),
        } satisfies DropdownOption,
      ],
    } satisfies DropdownConfig,
  ];
};

type ToViewpointModeRelationshipsViewActionsArgs = {
  dependencies: ViewpointModeRelationshipsSettingsControlsDependencies;
  i18nLocale: I18nLocale;
};

export const toViewpointModeRelationshipsViewActions = ({
  dependencies: { viewSettings, activeGroups, rootNode, viewInstanceId },
  i18nLocale,
}: ToViewpointModeRelationshipsViewActionsArgs): SettingsConfig[] => {
  const hasNoGroups = activeGroups.length === 0;

  return [
    getCollapseAllButtonConfig({
      viewSettings,
      isDisabled: hasNoGroups,
      rootNode,
      viewInstanceId,
      i18nLocale,
    }),
    getExpandAllButtonConfig({
      viewSettings,
      isDisabled: hasNoGroups,
      viewInstanceId,
      i18nLocale,
    }),
  ];
};
